import React, { useState } from 'react';
import {
  IAvailableWorkflowActions,
  ISubmissionFile
} from '@publishing-engine/domain/models';
import { FileListDisplay } from './FileListDisplay.js';
import { FileListEmptyState } from './FileListEmptyState.js';
import { Flex } from '@chakra-ui/react';
import { IFileValue } from './SourceFileListUploadModal.js';
import { FormFieldFileUploadArray } from '@publishing-engine/domain/types/article';
import { FieldHeader } from '../common/FieldHeader.js';
import { FileListDiffModal } from './FileListDiffModal.js';

import { EntryPoint } from 'src/utils/types.js';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import FileListUploadModal from './FileListUploadModal.js';
import { getFileListCount } from 'src/utils/fileCount';

export type FormFieldFileList = Omit<FormFieldFileUploadArray, 'component'> & {
  component: string;
};

export const getFileFieldConfig = ({
  name,
  fieldKey,
  validations
}: {
  name: string;
  fieldKey: string;
  validations: FormFieldFileUploadArray['params']['validations'];
}) => ({
  name,
  params: {
    validations,
    labels: {
      main: name,
      subtitle: ''
    }
  },
  caseDataFields: {
    main: fieldKey
  }
});

type IProps = {
  formFieldDefinition: FormFieldFileList;
  value?: ISubmissionFile[];
  previousValue?: ISubmissionFile[];
  isEditable?: boolean;
  referenceName?: string;
  onChange: (value: IFileValue) => void;
  submissionContextId: string;
  submissionId: string;
  showSubtitle: boolean;
  availableActions: IAvailableWorkflowActions;
  entryPoint: EntryPoint;
  uploadCtaCopy?: string;
  isUpdateRequired?: boolean;
};

export const FileList: React.FC<IProps> = ({
  formFieldDefinition,
  value,
  previousValue,
  isEditable,
  submissionContextId,
  onChange,
  referenceName,
  submissionId,
  showSubtitle,
  availableActions,
  entryPoint,
  uploadCtaCopy,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated, fieldsResponse } =
    useSubmissionFields(entryPoint);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);
  const hasFile = Array.isArray(value) && value.length > 0;

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);

  return (
    <Flex flexDirection={'column'} gap={2}>
      {isEditing && (
        <FileListUploadModal
          formField={formFieldDefinition}
          isOpen={isEditing}
          value={value}
          onClose={() => {
            setIsEditing(false);
            onchange;
          }}
          onSubmit={value => {
            setIsEditing(false);
            // @ts-ignore TODO: fix types
            onChange(value.fileSet[formFieldDefinition.name]);
          }}
          submissionContextId={submissionContextId}
          referenceName={referenceName}
          submissionId={submissionId}
          SubmissionFieldsResponse={fieldsResponse?.fields}
        />
      )}

      {isUpdated && showDiffModal ? (
        <FileListDiffModal
          config={formFieldDefinition}
          value={value}
          previousValue={previousValue}
          onClose={closeDiffModal}
          submissionContextId={submissionContextId}
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label={formFieldDefinition.params.labels.main}
        subtitle={
          showSubtitle ? formFieldDefinition.params.labels.subtitle : ''
        }
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        availableActions={availableActions}
        entryPoint={entryPoint}
        uploadCtaCopy={uploadCtaCopy}
        isUpdateRequired={isUpdateRequired}
        fileCount={getFileListCount(value)}
      />

      {hasFile ? (
        <FileListDisplay
          files={value}
          submissionContextId={submissionContextId}
        />
      ) : (
        <FileListEmptyState fieldLabels={formFieldDefinition.params.labels} />
      )}
    </Flex>
  );
};
